import React from 'react';
import {useParams,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import californiaLocations from '../data/californiaLocations';
import { trackButtonClick } from '../utils/tracking';

const {FiMapPin,FiShield,FiDollarSign,FiClock,FiCheck,FiStar}=FiIcons;

const CityLandingPage=()=> {
  const {citySlug}=useParams();
  const cityData=californiaLocations[citySlug];

  if (!cityData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">City Not Found</h1>
          <p className="text-gray-600 mb-8">The city you're looking for doesn't exist in our coverage area.</p>
          <Link
            to="/"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const benefits=[
    {icon: FiShield,title: `${cityData.name} Pet Protection`,description: `Comprehensive coverage for ${cityData.name} pets with up to 90% reimbursement`},
    {icon: FiDollarSign,title: 'Local Pricing',description: `Plans starting at $${cityData.averageCosts.dog}/month for dogs,$${cityData.averageCosts.cat}/month for cats`},
    {icon: FiClock,title: 'Fast Local Service',description: `Quick claims processing and local ${cityData.name} customer support`}
  ];

  const testimonials=[
    {name: 'Sarah M.',location: `${cityData.name},CA`,rating: 5,text: `Nationwide saved me thousands when my dog needed emergency surgery. Living in ${cityData.name},having reliable pet insurance is essential!`},
    {name: 'Mike D.',location: `${cityData.name},CA`,rating: 5,text: `Best pet insurance decision I made. The coverage works perfectly with all the great vets here in ${cityData.name}.`}
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <SafeIcon icon={FiMapPin} className="h-5 w-5 text-blue-300" />
                <span className="text-blue-200">Serving {cityData.name} & Surrounding Areas</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Pet Insurance {cityData.name} CA
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Protect your {cityData.name} pets with America's #1 pet insurance. Coverage for dogs,cats,birds & exotic pets near {cityData.landmarks.slice(0,2).join(',')}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick(`${cityData.name} Hero Get Quote`)}
                  className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors text-center"
                >
                  Get {cityData.name} Quote
                </a>
                <Link
                  to="/compare"
                  onClick={() => trackButtonClick(`${cityData.name} Hero Compare Plans`)}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors text-center"
                >
                  Compare Plans
                </Link>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">${cityData.averageCosts.dog}</div>
                  <div className="text-sm text-blue-200">Dog Plans From</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">${cityData.averageCosts.cat}</div>
                  <div className="text-sm text-blue-200">Cat Plans From</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">90%</div>
                  <div className="text-sm text-blue-200">Reimbursement</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=400&fit=crop&crop=center"
                alt={`Happy pet family in ${cityData.name},California with Nationwide Pet Insurance`}
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-gray-900 p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500" />
                  <span className="font-semibold">Protected in {cityData.name}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why {cityData.name} Pet Owners Choose Nationwide
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From {cityData.landmarks[0]} to {cityData.landmarks[1]},{cityData.name} pet owners trust Nationwide for comprehensive pet insurance coverage.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Landmarks & Coverage Area */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Pet Insurance Coverage Throughout {cityData.name}
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Areas We Serve</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {cityData.landmarks.map((landmark,index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <SafeIcon icon={FiMapPin} className="h-4 w-4 text-blue-600" />
                        <span className="text-gray-700">{landmark}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Partner Veterinary Clinics</h3>
                  <div className="space-y-2">
                    {cityData.veterinaryClinics.map((clinic,index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                        <span className="text-gray-700">{clinic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick(`${cityData.name} Coverage Area Get Quote`)}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
                >
                  Get {cityData.name} Quote Now
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=600&h=400&fit=crop&crop=center"
                alt={`Veterinarian examining pet in ${cityData.name},California`}
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What {cityData.name} Pet Owners Say
            </h2>
            <p className="text-xl text-gray-600">
              Real stories from satisfied customers in {cityData.name}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_,i) => (
                    <SafeIcon key={i} icon={FiStar} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Subcity Links */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pet Insurance in {cityData.name} Area Communities
            </h2>
            <p className="text-xl text-gray-600">
              We also serve these {cityData.name} area communities with comprehensive pet insurance coverage
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {cityData.subCities.map((subCity,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/pet-insurance/${citySlug}/${subCity.slug}`}
                  className="block bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center"
                >
                  <h3 className="font-semibold text-gray-900 mb-1">Pet Insurance {subCity.name}</h3>
                  <p className="text-sm text-gray-600">Coverage for {subCity.name} pets</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Protect Your {cityData.name} Pet?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Join thousands of {cityData.name} pet owners who trust America's #1 pet insurance provider for their pet's health and safety.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackButtonClick(`${cityData.name} Footer Get Quote`)}
                className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Get Instant {cityData.name} Quote
              </a>
              <Link
                to="/compare"
                onClick={() => trackButtonClick(`${cityData.name} Footer Compare Plans`)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                Compare Plans
              </Link>
            </div>
            <p className="mt-6 text-blue-200">
              Free quotes • No obligation • Instant coverage available in {cityData.name}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default CityLandingPage;